import React from 'react';
import { ColorPicker, Tooltip } from 'antd';
import { useAppStore } from '../../store/useAppStore';
import { C } from '../../theme';
import { BRUSH_MIN, BRUSH_MAX } from '../../paintingConstants';
import { sep, rowStyle, toolBtn } from './toolbarStyles';

interface Props {
  colorPanelRender: (node: React.ReactNode, info: any) => React.ReactNode;
  handleBrushColorChange: (color: any, info: any) => void;
}

const DrawEraseContextStrip: React.FC<Props> = ({ colorPanelRender, handleBrushColorChange }) => {
  const toolMode             = useAppStore((s) => s.toolMode);
  const drawProjectionMode   = useAppStore((s) => s.drawProjectionMode);
  const setDrawProjectionMode = useAppStore((s) => s.setDrawProjectionMode);
  const uvViewportMain       = useAppStore((s) => s.uvViewportMain);
  const inverseWarpK         = useAppStore((s) => s.inverseWarpK);
  const setInverseWarpK      = useAppStore((s) => s.setInverseWarpK);
  const inverseWarpStrength  = useAppStore((s) => s.inverseWarpStrength);
  const setInverseWarpStrength = useAppStore((s) => s.setInverseWarpStrength);
  const brushSize            = useAppStore((s) => s.brushSize);
  const setBrushSize         = useAppStore((s) => s.setBrushSize);
  const brushOpacity         = useAppStore((s) => s.brushOpacity);
  const setBrushOpacity      = useAppStore((s) => s.setBrushOpacity);
  const smoothness           = useAppStore((s) => s.smoothness);
  const setSmoothness        = useAppStore((s) => s.setSmoothness);
  const usePressure          = useAppStore((s) => s.usePressure);
  const setUsePressure       = useAppStore((s) => s.setUsePressure);
  const simPressure          = useAppStore((s) => s.simPressure);
  const setSimPressure       = useAppStore((s) => s.setSimPressure);
  const simPressureRandomness = useAppStore((s) => s.simPressureRandomness);
  const setSimPressureRandomness = useAppStore((s) => s.setSimPressureRandomness);
  const pickerValue          = useAppStore((s) => s.pickerValue);

  return (
    <div className="tb-row" style={{ ...rowStyle, minHeight: 28, borderLeft: `3px solid ${toolMode === 'draw' ? C.toolDraw : C.toolErase}`, paddingLeft: 5 }}>
      {/* Color picker — only for draw */}
      {toolMode === 'draw' && (
        <>
          <ColorPicker
            disabledAlpha
            mode={['single', 'gradient']}
            value={pickerValue}
            onChange={handleBrushColorChange}
            panelRender={colorPanelRender}
            size="small"
          />
          <div style={sep} />
        </>
      )}
      {([
        { m: 'wrap',    tip: 'Wrap projection — follows UV space' },
        { m: 'project', tip: uvViewportMain ? 'Project mode unavailable in UV view' : 'Project projection — follows camera view' },
        { m: 'inverse', tip: uvViewportMain ? 'Inverse mode unavailable in UV view' : 'Inverse projection — concave appearance' },
      ] as const).map(({ m, tip }) => (
        <Tooltip key={m} title={tip} placement="bottom">
          <button
            onClick={() => !uvViewportMain || m === 'wrap' ? setDrawProjectionMode(m) : undefined}
            disabled={uvViewportMain && m !== 'wrap'}
            style={{ ...toolBtn(drawProjectionMode === m, toolMode === 'draw' ? 'blue' : 'red'), ...(uvViewportMain && m !== 'wrap' ? { opacity: 0.35, cursor: 'not-allowed' } : {}) }}
          >{m}</button>
        </Tooltip>
      ))}
      {drawProjectionMode === 'inverse' && (
        <>
          <div style={sep} />
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>K</span>
          <Tooltip title={`Inverse warp K: ${inverseWarpK.toFixed(2)}`} placement="bottom">
            <input type="range" min={0.1} max={4} step={0.05} value={inverseWarpK}
              onChange={(e) => setInverseWarpK(parseFloat(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
            />
          </Tooltip>
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Str</span>
          <Tooltip title={`Inverse warp strength: ${inverseWarpStrength.toFixed(2)}`} placement="bottom">
            <input type="range" min={0} max={1} step={0.05} value={inverseWarpStrength}
              onChange={(e) => setInverseWarpStrength(parseFloat(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
            />
          </Tooltip>
        </>
      )}
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Size</span>
      <Tooltip title={`${Math.round((brushSize - BRUSH_MIN) / (BRUSH_MAX - BRUSH_MIN) * 100)}%`} placement="bottom">
        <input
          type="range" min={BRUSH_MIN} max={BRUSH_MAX} step={0.005} value={brushSize}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          style={{ width: 80, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
        />
      </Tooltip>
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Opacity</span>
      <Tooltip title={`${Math.round(brushOpacity * 100)}%`} placement="bottom">
        <input
          type="range" min={0} max={1} step={0.01} value={brushOpacity}
          onChange={(e) => setBrushOpacity(Number(e.target.value))}
          style={{ width: 70, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
        />
      </Tooltip>
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Smooth</span>
      <Tooltip title={`Smoothness: ${Math.round(smoothness * 100)}%`} placement="bottom">
        <input
          type="range" min={0} max={1} step={0.01} value={smoothness}
          onChange={(e) => setSmoothness(Number(e.target.value))}
          style={{ width: 70, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
        />
      </Tooltip>
      <div style={sep} />
      <Tooltip title="Pressure sensitivity" placement="bottom">
        <button
          onClick={() => { setUsePressure(!usePressure); if (usePressure) setSimPressure(false); }}
          style={toolBtn(usePressure, toolMode === 'draw' ? 'blue' : 'red')}
        >
          Pressure
        </button>
      </Tooltip>
      {/* {usePressure && (
        <Tooltip title="Simulate pressure (sine wave)" placement="bottom">
          <button
            onClick={() => setSimPressure(!simPressure)}
            style={toolBtn(simPressure, toolMode === 'draw' ? 'blue' : 'red')}
          >
            Sim
          </button>
        </Tooltip>
      )} */}
      {usePressure && simPressure && (
        <>
          <div style={sep} />
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Freq</span>
          <Tooltip title={`Pressure frequency: ${Math.round(simPressureRandomness * 100)}%`} placement="bottom">
            <input
              type="range" min={0} max={1} step={0.01} value={simPressureRandomness}
              onChange={(e) => setSimPressureRandomness(Number(e.target.value))}
              style={{ width: 70, cursor: 'pointer', accentColor: toolMode === 'draw' ? C.toolDraw : C.toolErase }}
            />
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default React.memo(DrawEraseContextStrip);
